import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Friedhof, CreateFriedhofRequest, FRIEDHOF_STATUS } from '../types';
import friedhofService from '../services/friedhofService';
import { useFriedhof } from '../contexts/FriedhofContext';
import { useAuth } from '../contexts/AuthContext';
import { brand } from '../config/brand';
import './FriedhofAuswahl.css';
import { Icon } from '../components/ui';

const statusLabels: Record<string, string> = {
  AKTIV: 'Aktiv', INAKTIV: 'Inaktiv', VOLL_BELEGT: 'Voll belegt'
};

const emptyForm = { name: '', adresse: '', anzahlGrabstaetten: '', status: 'AKTIV', notizen: '' };

const FriedhofAuswahl: React.FC = () => {
  const navigate = useNavigate();
  const { selectFriedhof, friedhofId, setAnzahlFriedhoefe } = useFriedhof();
  const { user, isAdmin } = useAuth();
  const [friedhoefe, setFriedhoefe] = useState<Friedhof[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Einmal beim Öffnen laden.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const liste = await friedhofService.getAll();
      setAnzahlFriedhoefe(liste.length);
      // Nur ein Friedhof: nichts zu wählen – direkt weiter, und zwar auf die angefragte Seite
      // (die Auswahl wird an ihrer Stelle angezeigt, deshalb ohne navigate).
      if (liste.length === 1) { selectFriedhof(liste[0].id, liste[0].name); return; }
      setFriedhoefe(liste);
    }
    catch { toast.error('Friedhöfe konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  const handleSelect = (f: Friedhof) => {
    selectFriedhof(f.id, f.name);
    navigate('/dashboard', { replace: true });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data: CreateFriedhofRequest = {
        name: formData.name,
        adresse: formData.adresse,
        anzahlGrabstaetten: parseInt(formData.anzahlGrabstaetten) || 0,
        status: formData.status,
        notizen: formData.notizen || undefined,
      };
      const created = await friedhofService.create(data);
      toast.success('Friedhof angelegt');
      setShowForm(false);
      setFormData(emptyForm);
      handleSelect(created);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Friedhof konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  const renderAddCard = (idx: number) => (
    <button
      type="button"
      className="friedhof-card friedhof-card--add"
      style={{ animationDelay: `${0.12 + idx * 0.08}s` }}
      onClick={() => setShowForm(true)}
    >
      <span className="friedhof-card__glow" aria-hidden="true" />
      <span className="friedhof-card__icon friedhof-card__icon--add">+</span>
      <span className="friedhof-card__label">Friedhof anlegen</span>
      <span className="friedhof-card__meta">
        {friedhoefe.length === 0
          ? 'Legen Sie Ihren ersten Friedhof an, um loszulegen.'
          : 'Einen weiteren Friedhof anlegen.'}
      </span>
    </button>
  );

  return (
    <div className="friedhof-select">
      <div className="friedhof-select__aurora" aria-hidden="true" />

      <header className="friedhof-select__header">
        <div className="friedhof-select__brand">{brand.appName}</div>
        <h1 className="friedhof-select__title">
          {user?.firstName ? `Willkommen, ${user.firstName}.` : 'Willkommen.'}
        </h1>
        <p className="friedhof-select__subtitle">Welchen Friedhof möchten Sie verwalten?</p>
      </header>

      {loading ? (
        <div className="friedhof-select__spinner" />
      ) : friedhoefe.length === 0 && !isAdmin() ? (
        <p className="friedhof-select__empty">
          Es sind noch keine Friedhöfe angelegt. Bitte wenden Sie sich an einen Administrator.
        </p>
      ) : (
        <div className="friedhof-select__cards">
          {friedhoefe.map((f, idx) => (
            <button
              key={f.id}
              type="button"
              className={`friedhof-card ${friedhofId === f.id ? 'friedhof-card--last' : ''}`}
              style={{ animationDelay: `${0.12 + idx * 0.08}s` }}
              onClick={() => handleSelect(f)}
            >
              <span className="friedhof-card__glow" aria-hidden="true" />
              <span className="friedhof-card__icon"><Icon.Baum className="h-7 w-7" /></span>
              <span className="friedhof-card__label">{f.name}</span>
              <span className="friedhof-card__meta">{f.adresse}</span>
              <span className="friedhof-card__stats">
                {f.anzahlGrabstaetten} Grabstätten · {statusLabels[f.status] || f.status}
                {friedhofId === f.id && <span className="friedhof-card__last-badge">zuletzt</span>}
              </span>
              <span className="friedhof-card__cta">
                Öffnen
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
          ))}
          {isAdmin() && renderAddCard(friedhoefe.length)}
        </div>
      )}

      <p className="friedhof-select__hint">
        Der Friedhof lässt sich jederzeit unten in der Seitenleiste wechseln.
      </p>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto text-gray-900">
            <h2 className="text-lg font-semibold mb-4">Neuer Friedhof</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input required autoFocus value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Adresse *</label>
                <textarea required value={formData.adresse}
                  onChange={e => setFormData({ ...formData, adresse: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Anzahl Grabstätten *</label>
                  <input type="number" min={0} required value={formData.anzahlGrabstaetten}
                    onChange={e => setFormData({ ...formData, anzahlGrabstaetten: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status *</label>
                  <select value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg">
                    {FRIEDHOF_STATUS.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Notizen</label>
                <textarea value={formData.notizen}
                  onChange={e => setFormData({ ...formData, notizen: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg" rows={2} />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setShowForm(false); setFormData(emptyForm); }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Abbrechen</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-60">
                  {saving ? 'Wird angelegt…' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriedhofAuswahl;
